import { useEffect, useState, useMemo } from 'react';
import {
  Card,
  Form,
  Select,
  DatePicker,
  Input,
  InputNumber,
  Switch,
  Button,
  Space,
  Typography,
  Empty,
  Alert,
  Divider,
} from 'antd';
import { FileSearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  MarginStatus,
  HistoTumorType,
  type Biopsy,
  type HistopathologyDiagnostic,
  type CreateHistopathologyDto,
} from '../../../../api/dermatoApi';
import { useDermatoWizard, useRegisterStepSave } from '../DermatoWizardContext';
import dermatoApi from '../../../../api/dermatoApi';

const { Text } = Typography;

const MARGIN_OPTIONS = Object.values(MarginStatus).map((v) => ({
  value: v,
  label:
    v === 'FREE' ? 'Margini libere'
    : v === 'CLOSE' ? 'Margini apropiate'
    : v === 'INVOLVED' ? 'Margini invadate'
    : 'Neevaluate',
}));

const TUMOR_OPTIONS = Object.values(HistoTumorType).map((v) => ({
  value: v,
  label:
    v === 'BENIGN_NEVUS' ? 'Nevus benign'
    : v === 'MELANOMA_MALIGNANT' ? 'Melanom malign'
    : v === 'BASAL_CELL_CARCINOMA' ? 'Carcinom bazocelular'
    : v === 'SQUAMOUS_CELL_CARCINOMA' ? 'Carcinom scuamocelular'
    : v === 'DERMATOFIBROMA' ? 'Dermatofibrom'
    : v === 'KERATOACANTHOMA' ? 'Keratoacantom'
    : v === 'ACTINIC_KERATOSIS' ? 'Keratoză actinică'
    : 'Alt tip',
}));

const Step10Histopathology = () => {
  const { lesions, selectedLesionId, selectLesion, loading } = useDermatoWizard();
  const [biopsies, setBiopsies] = useState<Biopsy[]>([]);
  const [chosenBiopsyId, setChosenBiopsyId] = useState<string | null>(null);
  const [hp, setHp] = useState<HistopathologyDiagnostic | null>(null);
  const [loadingList, setLoadingList] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formAntd] = Form.useForm<CreateHistopathologyDto>();

  const lesion = useMemo(
    () => lesions.find((l) => l.id === selectedLesionId) ?? null,
    [lesions, selectedLesionId]
  );

  useEffect(() => {
    if (!lesion?.id) {
      setBiopsies([]);
      setChosenBiopsyId(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoadingList(true);
      try {
        const list = await dermatoApi.biopsy.getByLesion(lesion.id!);
        if (cancelled) return;
        setBiopsies(list);
        if (list.length > 0) {
          setChosenBiopsyId((prev) => prev ?? list[0].id);
        } else {
          setChosenBiopsyId(null);
        }
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [lesion?.id]);

  const biopsy = useMemo(
    () => biopsies.find((b) => b.id === chosenBiopsyId) ?? null,
    [biopsies, chosenBiopsyId]
  );

  useEffect(() => {
    if (!biopsy?.id) {
      setHp(null);
      formAntd.resetFields();
      return;
    }
    let cancelled = false;
    const load = async () => {
      const v = await dermatoApi.histopathology.getByBiopsy(biopsy.id).catch(() => null);
      if (cancelled) return;
      setHp(v);
      if (v) {
        formAntd.setFieldsValue({
          ...v,
          resultDate: dayjs(v.resultDate),
        } as any);
      } else {
        formAntd.resetFields();
        formAntd.setFieldsValue({
          resultDate: dayjs(),
          pathologist: '',
          sampleType: biopsy.sampleType,
          sampleCode: biopsy.sampleCode,
          diagnosis: '',
          tumorType: HistoTumorType.BENIGN_NEVUS,
          margins: MarginStatus.FREE,
        } as any);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [biopsy?.id, formAntd]);

  const persist = async (): Promise<boolean> => {
    if (!biopsy?.id) return true;
    try {
      const values = await formAntd.validateFields();
      setSaving(true);
      const dto: CreateHistopathologyDto = {
        ...values,
        resultDate: (values.resultDate as unknown as dayjs.Dayjs).toISOString(),
      };
      const saved = hp?.id
        ? await dermatoApi.histopathology.update(hp.id, dto)
        : await dermatoApi.histopathology.createForBiopsy(biopsy.id, dto);
      setHp(saved);
      return true;
    } catch {
      // validation
      return false;
    } finally {
      setSaving(false);
    }
  };

  useRegisterStepSave(persist);

  if (loading) return <Card loading title="Histopatologie diagnostică" />;

  if (lesions.length === 0) {
    return (
      <Card title="Histopatologie diagnostică">
        <Empty description="Nu există leziuni. Mergi la pasul 3." />
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space>
          <FileSearchOutlined style={{ color: '#1890ff' }} />
          <span>Histopatologie diagnostică — 1:1 per biopsie</span>
        </Space>
      }
    >
      <Form.Item label="Selectează leziunea" required style={{ marginBottom: 16 }}>
        <Select
          value={selectedLesionId ?? undefined}
          onChange={selectLesion}
          placeholder="Alege o leziune"
          options={lesions.map((l) => ({
            value: l.id,
            label: `${l.clinicalCode} — ${l.location}`,
          }))}
        />
      </Form.Item>

      {!lesion && <Alert type="warning" showIcon message="Selectează o leziune." />}

      {lesion && loadingList && <Alert type="info" message="Se încarcă biopsiile…" />}

      {lesion && !loadingList && biopsies.length === 0 && (
        <Empty description="Nu există biopsii pt această leziune. Mergi la pasul 9." />
      )}

      {lesion && !loadingList && biopsies.length > 0 && (
        <>
          <Form.Item label="Selectează biopsia" required style={{ marginBottom: 16 }}>
            <Select
              value={chosenBiopsyId ?? undefined}
              onChange={setChosenBiopsyId}
              options={biopsies.map((b) => ({
                value: b.id,
                label: `${b.sampleCode} (${new Date(b.biopsyDate).toLocaleDateString('ro-RO')}) — ${b.procedureType}`,
              }))}
            />
          </Form.Item>

          {biopsy && (
            <Form form={formAntd} layout="vertical">
              <Form.Item name="resultDate" label="Data rezultatului" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="pathologist" label="Anatomopatolog" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="sampleType" label="Tip probă" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="sampleCode" label="Cod probă" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="diagnosis" label="Diagnostic histopatologic" rules={[{ required: true }]}>
                <Input.TextArea rows={3} placeholder="Descriere microscopică, pattern-uri, atipii…" />
              </Form.Item>
              <Form.Item name="tumorType" label="Tip tumoral" rules={[{ required: true }]}>
                <Select options={TUMOR_OPTIONS} />
              </Form.Item>
              <Form.Item name="breslowMm" label="Grosime Breslow (mm)">
                <InputNumber min={0} step={0.1} style={{ width: '100%' }} placeholder="ex: 0.8" />
              </Form.Item>
              <Form.Item name="ulceration" label="Ulcerație" valuePropName="checked">
                <Switch />
              </Form.Item>
              <Form.Item name="margins" label="Status margini" rules={[{ required: true }]}>
                <Select options={MARGIN_OPTIONS} />
              </Form.Item>
              <Form.Item name="observations" label="Observații">
                <Input.TextArea rows={2} />
              </Form.Item>
              <Divider />
              <Form.Item>
                <Space>
                  <Button type="primary" onClick={() => void persist()} loading={saving}>
                    {hp ? 'Actualizează' : 'Salvează HP'}
                  </Button>
                  {hp && <Text type="success">✓ HP existent — {hp.id.slice(0, 8)}</Text>}
                </Space>
              </Form.Item>
            </Form>
          )}
        </>
      )}
    </Card>
  );
};

export default Step10Histopathology;
